import { useState } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { EyeIcon, EyeOffIcon } from '../../components/ui/IconField';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../context/ThemeContext';
import { api } from '../../lib/api';
import { roleLabel, isAdminOrAbove } from '../../lib/roles';

// Light-themed password input (IconField is dark-styled for the auth pages).
function PwInput({ show, setShow, ...props }) {
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        autoComplete="new-password"
        className="w-full rounded-xl border border-slate-200 dark:border-white/15 bg-white/70 dark:bg-slate-800/70 py-2.5 pl-3 pr-10 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500
          transition focus:border-brand focus:bg-white dark:focus:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand/30"
        {...props}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

// A live password-requirement row: green check when met, muted circle when not.
function Req({ ok, children }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'}`}>
      <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] leading-none ${ok ? 'bg-green-500 text-white' : 'bg-slate-200 text-transparent dark:bg-white/10'}`}>✓</span>
      {children}
    </li>
  );
}

// A labelled metadata cell for the account header.
function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-navy-800 dark:text-slate-100">{value}</div>
    </div>
  );
}

const SunIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="10" cy="10" r="3.4" /><path d="M10 1.5v2M10 16.5v2M1.5 10h2M16.5 10h2M4.1 4.1l1.4 1.4M14.5 14.5l1.4 1.4M4.1 15.9l1.4-1.4M14.5 5.5l1.4-1.4" strokeLinecap="round" /></svg>
);
const MoonIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M16.5 11.8A6.5 6.5 0 018.2 3.5a6.5 6.5 0 108.3 8.3z" strokeLinejoin="round" /></svg>
);

export default function Profile() {
  const { profile, session, updatePassword, refreshProfile } = useAuth();
  const { theme, setTheme } = useTheme();

  // Edit-profile state
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameDone, setNameDone] = useState(false);

  // Change-password state
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const displayName = profile?.fullName || profile?.email || 'Account';
  const initial = (profile?.fullName || profile?.email || '?').charAt(0);
  const roleText = roleLabel(profile?.role);

  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
  const memberSince = fmtDate(session?.user?.created_at);
  const lastSignIn = fmtDate(session?.user?.last_sign_in_at);
  const resellerDisplay = isAdminOrAbove(profile) ? 'All resellers (full access)' : (profile?.resellerName || '—');

  // Live password validation for the Security form.
  const pwLongEnough = password.length >= 8;
  const pwMatch = confirm.length > 0 && password === confirm;
  const pwValid = pwLongEnough && pwMatch;

  const startEdit = () => {
    setName(profile?.fullName || '');
    setNameError('');
    setNameDone(false);
    setEditing(true);
  };

  const saveName = async (e) => {
    e.preventDefault();
    setNameError('');
    if (!name.trim()) { setNameError('Full name is required.'); return; }
    setSavingName(true);
    try {
      await api.patch('/api/auth/me', { fullName: name.trim() });
      await refreshProfile();
      setNameDone(true);
      setEditing(false);
    } catch (err) {
      setNameError(err.message);
    } finally {
      setSavingName(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError('');
    setDone(false);
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await updatePassword(password);
      setDone(true);
      setPassword('');
      setConfirm('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Profile">
      <div className="max-w-6xl space-y-6">
        {/* Account header */}
        <Card
          title="Account"
          actions={!editing && (
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-brand transition hover:bg-brand/10 dark:hover:bg-brand/20"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path strokeLinecap="round" strokeLinejoin="round" d="M4 13.5V16h2.5l7.4-7.4-2.5-2.5L4 13.5zM12.5 5.1l2.4 2.4" /></svg>
              Edit profile
            </button>
          )}
        >
          {nameDone && !editing && (
            <div className="mb-4 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700 ring-1 ring-green-200 dark:bg-green-500/15 dark:text-green-300 dark:ring-green-400/30">
              Profile updated.
            </div>
          )}

          {editing ? (
            <form onSubmit={saveName} className="space-y-4">
              {nameError && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-200 dark:bg-red-500/15 dark:text-red-300 dark:ring-red-400/30">{nameError}</div>
              )}
              <div className="max-w-sm">
                <label className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Full name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white/70 px-3 py-2.5 text-slate-800 transition placeholder:text-slate-400 focus:border-brand focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand/30 dark:border-white/15 dark:bg-slate-800/70 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:bg-slate-800"
                  placeholder="Your name"
                  autoFocus
                />
              </div>
              <div className="max-w-sm">
                <label className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Email</label>
                <input value={profile?.email || ''} disabled className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-slate-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-500" />
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Email is used for sign-in and can't be changed here.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button type="submit" disabled={savingName}>{savingName ? 'Saving…' : 'Save changes'}</Button>
                <Button type="button" variant="secondary" onClick={() => setEditing(false)} disabled={savingName}>Cancel</Button>
              </div>
            </form>
          ) : (
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
              <div className="flex items-center gap-4">
                <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-brand-gradient text-3xl font-semibold uppercase text-white shadow-brand">
                  {initial}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-xl font-semibold text-navy-800 dark:text-slate-100">{displayName}</div>
                  {profile?.email && <div className="truncate text-sm text-slate-500 dark:text-slate-400">{profile.email}</div>}
                  {roleText && (
                    <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-xs font-medium capitalize text-brand-dark dark:bg-brand/20 dark:text-brand-light">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                      {roleText}
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-10 gap-y-4 border-t border-white/50 pt-5 dark:border-white/10 sm:grid-cols-4 lg:ml-auto lg:border-0 lg:pt-0">
                <Meta label="Reseller" value={resellerDisplay} />
                <Meta label="Status" value={profile?.status === 'inactive' ? 'Inactive' : 'Active'} />
                <Meta label="Member since" value={memberSince} />
                <Meta label="Last sign-in" value={lastSignIn} />
              </div>
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
          {/* Security */}
          <Card title="Security">
            <p className="text-sm text-slate-500 dark:text-slate-400">Set a new password for your account.</p>

            {error && (
              <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-200 dark:bg-red-500/15 dark:text-red-300 dark:ring-red-400/30">{error}</div>
            )}
            {done && (
              <div className="mt-4 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700 ring-1 ring-green-200 dark:bg-green-500/15 dark:text-green-300 dark:ring-green-400/30">
                Password updated successfully.
              </div>
            )}

            <form onSubmit={submitPassword} className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">New password</label>
                <PwInput show={show} setShow={setShow} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600 dark:text-slate-300">Confirm password</label>
                <PwInput show={show} setShow={setShow} placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </div>
              {(password || confirm) && (
                <ul className="space-y-1.5 text-xs">
                  <Req ok={pwLongEnough}>At least 8 characters</Req>
                  <Req ok={pwMatch}>Both passwords match</Req>
                </ul>
              )}
              <Button type="submit" disabled={busy || !pwValid}>{busy ? 'Updating…' : 'Update password'}</Button>
            </form>
          </Card>

          {/* Preferences */}
          <Card title="Preferences">
            <div>
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Appearance</div>
              <p className="mb-3 mt-0.5 text-xs text-slate-400 dark:text-slate-500">Choose how the dashboard looks. Saved on this device.</p>
              <div className="inline-flex rounded-xl border border-slate-200 p-1 dark:border-white/15">
                {[{ key: 'light', label: 'Light', icon: <SunIcon /> }, { key: 'dark', label: 'Dark', icon: <MoonIcon /> }].map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setTheme(o.key)}
                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${theme === o.key ? 'bg-brand text-white shadow-brand' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10'}`}
                  >
                    {o.icon}
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
